import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Shield, Leaf, Clock, CheckCircle, Star, Home as HomeIcon, Search, MousePointer, AlertTriangle, Bug, Layers, CircleDot, Triangle, Network, Wind, Hexagon, Bird, Droplet, Squirrel, Building } from 'lucide-react';
import { companyInfo, services } from '../mock';
import LeadForm from '../components/LeadForm';
import TrustBadges from '../components/TrustBadges';
import FeaturedIn from '../components/FeaturedIn';
import SchemaMarkup from '../components/SchemaMarkup';
import SEOHead from '../components/SEOHead';
import AdvancedSEO from '../components/AdvancedSEO';
import GoogleMap from '../components/GoogleMap';
import TestimonialSEO from '../components/TestimonialSEO';
import UnifiedFAQSEO from '../components/UnifiedFAQSEO';
import ComprehensiveSEO from '../components/ComprehensiveSEO';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [faqsFromAPI, setFaqsFromAPI] = useState([]);

  useEffect(() => {
    fetchTestimonials();
    fetchFAQs();
  }, []);

  const fetchTestimonials = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/testimonials`);
      setTestimonials(response.data);
    } catch (error) {
      console.error('Failed to fetch testimonials:', error);
    }
  };

  const fetchFAQs = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/faqs`);
      setFaqsFromAPI(response.data);
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
    }
  };
  // SEO Data
  const breadcrumbs = [
    { name: "Home", url: "https://nypestexterminating.com" }
  ];

  // Use API FAQs if available, fallback to hardcoded for SEO
  const faqData = faqsFromAPI.length > 0 ? faqsFromAPI : [
    {
      question: "What makes NY Pest & Exterminating different from other pest control companies?",
      answer: "We specialize in 100% eco-friendly and pet-safe solutions. Our Green Shield certified methods are effective against pests while being completely safe for your family, pets, and the environment. We offer same-day service and 24/7 availability across all NYC boroughs."
    },
    {
      question: "Do you provide same-day pest control service in NYC?",
      answer: "Yes! We offer same-day emergency pest control services throughout New York City. Call (646) 209-9955 and we typically arrive within 2-4 hours for urgent pest situations."
    },
    {
      question: "Are your pest control methods safe for children and pets?",
      answer: "Absolutely! All our treatments are 100% eco-friendly and pet-safe. We use plant-based, non-toxic solutions that eliminate pests without harming your family or pets. We're Green Shield certified for environmental safety."
    },
    {
      question: "Which areas in NYC do you serve?",
      answer: "We provide pest control services throughout all 5 NYC boroughs: Manhattan, Brooklyn, Queens, The Bronx, and Staten Island. We also serve Nassau County, Suffolk County, and Westchester County."
    },
    {
      question: "How much does pest control cost in NYC?",
      answer: "Pest control costs vary depending on the type of pest, property size, and severity of infestation. Contact us at (646) 209-9955 for a free, no-obligation quote. We offer competitive pricing with satisfaction guaranteed."
    },
    {
      question: "Do you offer free pest inspections?",
      answer: "Yes, we provide free pest inspections and quotes. Our licensed technicians will assess your property and provide a detailed treatment plan with transparent pricing."
    }
  ];

  const localBusinessData = {
    name: "NY Pest & Exterminating",
    phone: "(646) 209-9955",
    address: "New York City, NY",
    services: ["Rodent Control", "Bed Bug Treatment", "Termite Inspection", "Commercial Pest Control"]
  };

  return (
    <div>
      {/* Comprehensive SEO for all platforms and NYC zip codes */}
      <ComprehensiveSEO />
      
      <AdvancedSEO 
        title="NY Pest & Exterminating | #1 Eco-Friendly Pest Control NYC | Same-Day Service | Licensed & Insured"
        description="★★★★★ NYC's top-rated eco-friendly pest control experts. Same-day service for rats, mice, bed bugs, termites & more. 100% pet-safe solutions. Serving all 5 boroughs + Long Island. Call (646) 209-9955 for free quote!"
        keywords="pest control NYC, exterminator New York City, bed bugs NYC same day, rodent control Manhattan, termite inspection Brooklyn, eco friendly pest control Queens, emergency exterminator Bronx, Staten Island pest control, commercial pest control NYC, Green Shield certified pest control, pet safe exterminator NYC, 24/7 pest control New York, licensed pest control NYC, bed bug heat treatment NYC, mouse exterminator NYC, rat control NYC, cockroach exterminator NYC"
        url="https://nypestexterminating.com"
        breadcrumbs={breadcrumbs}
        faqData={faqData}
        localBusinessData={localBusinessData}
        includeFAQSchema={false}
      />
      <SEOHead 
        title="NY Pest & Exterminating | #1 Eco-Friendly Pest Control NYC | Same-Day Service"
        description="★★★★★ NYC's top-rated eco-friendly pest control experts. Same-day service for rats, mice, bed bugs, termites & more. 100% pet-safe solutions. Call (646) 209-9955"
        keywords="pest control NYC, exterminator New York City, bed bugs NYC, rodent control Manhattan, termite inspection Brooklyn, eco friendly pest control Queens, emergency exterminator Bronx, Staten Island pest control"
        url="https://nypestexterminating.com"
      />
      <SchemaMarkup />
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-content">
          {/* Professional Pest Control Image */}
          <div className="flex justify-center mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_ecopestcontrol/artifacts/7osc181x_WhyProfessionalPestControlServicesareVitalforHomes.jpg"
              alt="Why Professional Pest Control Services are Vital for Homes - NY Pest & Exterminating"
              className="rounded-lg shadow-lg"
              style={{ maxWidth: '500px', maxHeight: '280px', width: '100%', objectFit: 'cover' }}
            />
          </div>
          
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: 'var(--accent-wash)', border: '1px solid var(--accent-primary)' }}>
            <Leaf size={16} style={{ color: 'var(--accent-text)' }} />
            <span className="body-small font-semibold" style={{ color: 'var(--accent-text)' }}>
              {companyInfo.usp}
            </span>
          </div>
          
          <h1 className="heading-1 mb-6">
            NYC's Trusted Pest Control Experts
          </h1>
          
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
            {companyInfo.uspSubtitle} - Serving all five boroughs and surrounding counties with same-day service.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a href={`tel:${companyInfo.phoneRaw}`} className="btn-primary text-lg px-8">
              <Phone size={20} />
              Call {companyInfo.phone}
            </a>
            <Link to="/contact" className="btn-secondary text-lg px-8">
              Get Free Quote
            </Link>
          </div>
          
          <div className="flex flex-wrap justify-center gap-6 mt-12">
            <div className="flex items-center gap-2">
              <CheckCircle size={20} style={{ color: 'var(--accent-text)' }} />
              <span className="body-small">Same-Day Service</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={20} style={{ color: 'var(--accent-text)' }} />
              <span className="body-small">100% Eco-Friendly</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle size={20} style={{ color: 'var(--accent-text)' }} />
              <span className="body-small">Licensed & Insured</span>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Professional Pest Control Services in NYC</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Comprehensive eco-friendly pest control solutions for all NYC boroughs - same-day service available
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => {
              const IconComponent = service.icon === 'MousePointer' ? MousePointer :
                                   service.icon === 'Search' ? Search :
                                   service.icon === 'Shield' ? Shield :
                                   service.icon === 'Home' ? HomeIcon :
                                   service.icon === 'AlertTriangle' ? AlertTriangle :
                                   service.icon === 'Bug' ? Bug :
                                   service.icon === 'Layers' ? Layers :
                                   service.icon === 'CircleDot' ? CircleDot :
                                   service.icon === 'Triangle' ? Triangle :
                                   service.icon === 'Network' ? Network :
                                   service.icon === 'Wind' ? Wind :
                                   service.icon === 'Hexagon' ? Hexagon :
                                   service.icon === 'Bird' ? Bird :
                                   service.icon === 'Droplet' ? Droplet :
                                   service.icon === 'Home' ? HomeIcon :
                                   service.icon === 'Squirrel' ? Squirrel :
                                   service.icon === 'Building' ? Building : Shield;
              
              // Special featured treatment for rats & mice
              if (service.id === 'rats-mice') {
                return (
                  <div key={service.id} className="md:col-span-2 lg:col-span-3">
                    <div className="product-card card-hover-lift p-8" style={{ background: 'linear-gradient(135deg, var(--accent-wash) 0%, var(--bg-card) 100%)', border: '2px solid var(--accent-primary)' }}>
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                        {/* Content Column */}
                        <div>
                          <div className="flex items-center gap-4 mb-4">
                            <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-primary)' }}>
                              <IconComponent size={32} color="white" />
                            </div>
                            <div>
                              <h3 className="text-2xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>Featured Service: {service.title}</h3>
                              <p className="text-lg font-semibold" style={{ color: 'var(--accent-text)' }}>NYC's #1 Rodent Elimination Experts</p>
                            </div>
                          </div>
                          
                          {/* Preview of comprehensive description */}
                          <div className="mb-6">
                            <h4 className="text-lg font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
                              Rodent Extermination NYC: Ultimate Guide to Expert Mice & Rat Removal
                            </h4>
                            <p className="body-text mb-4" style={{ color: 'var(--text-secondary)' }}>
                              Welcome to the definitive resource for professional rodent extermination in New York City. If you're searching for "rodent control NYC," "emergency rat exterminator," or "mice removal near me," you've found your solution.
                            </p>
                            
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                              <div className="flex items-start gap-2">
                                <div className="w-2 h-2 rounded-full mt-2" style={{ background: 'var(--accent-primary)' }}></div>
                                <span className="body-small">24/7 Emergency Response</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <div className="w-2 h-2 rounded-full mt-2" style={{ background: 'var(--accent-primary)' }}></div>
                                <span className="body-small">5-Step Eradication System</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <div className="w-2 h-2 rounded-full mt-2" style={{ background: 'var(--accent-primary)' }}></div>
                                <span className="body-small">Same-Day Service Available</span>
                              </div>
                              <div className="flex items-start gap-2">
                                <div className="w-2 h-2 rounded-full mt-2" style={{ background: 'var(--accent-primary)' }}></div>
                                <span className="body-small">Eco-Friendly & Pet Safe</span>
                              </div>
                            </div>
                            
                            <div className="p-4 rounded-lg mb-4" style={{ background: 'var(--bg-section)' }}>
                              <p className="body-small font-semibold mb-2" style={{ color: 'var(--accent-text)' }}>
                                🚨 Emergency Rodent Problem? We Provide 24/7 Fast Response in NYC
                              </p>
                              <p className="body-small" style={{ color: 'var(--text-secondary)' }}>
                                Same-Day Service Available | Free Inspections & Quotes | Satisfaction Guaranteed
                              </p>
                            </div>
                          </div>
                          
                          {/* Professional Rodent Image - Bottom of Description */}
                          <div className="mb-6">
                            <img 
                              src={service.image}
                              alt="Professional rodent extermination in NYC - Rats and mice removal experts"
                              title="Rats & Mice Extermination - NY Pest & Exterminating"
                              className="w-full rounded-lg shadow-lg"
                              style={{ maxHeight: '280px', objectFit: 'cover' }}
                            />
                          </div>
                          
                          <div className="flex flex-col sm:flex-row gap-4">
                            <Link 
                              to={`/services/${service.id}`}
                              className="btn-primary text-center"
                            >
                              Read Complete Guide →
                            </Link>
                            <a href={`tel:${companyInfo.phoneRaw}`} className="btn-secondary text-center">
                              <Phone size={20} />
                              Call Now: {companyInfo.phone}
                            </a>
                          </div>
                        </div>
                        
                        {/* Image Column */}
                        <div>
                          <div className="w-full h-64 lg:h-80 rounded-lg overflow-hidden shadow-lg">
                            <img 
                              src={service.image} 
                              alt="Professional rodent extermination in NYC - Rats and mice removal"
                              title="Rats & Mice Extermination - NY Pest & Exterminating"
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              }
              
              // Regular service cards for other services
              return (
                <Link
                  key={service.id}
                  to={`/services/${service.id}`}
                  className="product-card card-hover-lift"
                >
                  {/* Service Image */}
                  <div className="w-full h-48 mb-4 rounded-lg overflow-hidden" style={{ background: 'var(--bg-section)' }}>
                    <img 
                      src={service.image} 
                      alt={`${service.title} pest control service in NYC - Professional extermination`}
                      title={`${service.title} - NY Pest & Exterminating`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                      <IconComponent size={24} style={{ color: 'var(--accent-text)' }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="heading-3 mb-2">{service.title}</h3>
                      <p className="body-small">{service.description.split('\n')[0]}</p>
                    </div>
                  </div>
                  
                  <ul className="space-y-2">
                    {service.features.slice(0, 3).map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2">
                        <CheckCircle size={16} style={{ color: 'var(--accent-text)' }} />
                        <span className="body-small">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <div className="mt-4 flex items-center gap-2" style={{ color: 'var(--accent-text)' }}>
                    <span className="body-small font-semibold">Learn More →</span>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Why Choose NY Pest & Exterminating?</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Your safety and satisfaction are our top priorities
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                <Leaf size={32} style={{ color: 'var(--accent-text)' }} />
              </div>
              <h3 className="heading-3 mb-3">Eco-Friendly Methods</h3>
              <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                Green Shield Approach using non-toxic, plant-based solutions safe for your family and pets.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                <Clock size={32} style={{ color: 'var(--accent-text)' }} />
              </div>
              <h3 className="heading-3 mb-3">Rapid Response</h3>
              <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                Same-day service available for urgent pest emergencies. We're here when you need us most.
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
                <Shield size={32} style={{ color: 'var(--accent-text)' }} />
              </div>
              <h3 className="heading-3 mb-3">Satisfaction Guaranteed</h3>
              <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>
                If pests return, so do we - free of charge. Your complete satisfaction guaranteed.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Lead Form Section */}
      <section className="py-20" style={{ background: 'var(--bg-page)' }}>
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Side - Content */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ background: 'var(--accent-wash)', border: '1px solid var(--accent-primary)' }}>
                <Leaf size={16} style={{ color: 'var(--accent-text)' }} />
                <span className="body-small font-semibold" style={{ color: 'var(--accent-text)' }}>
                  Free Quote - No Obligation
                </span>
              </div>
              
              <h2 className="heading-2 mb-4">Ready to Eliminate Your Pest Problem?</h2>
              <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
                Get a free, no-obligation quote from NYC's eco-friendly pest control experts. A pest professional will contact you shortly with same-day service available.
              </p>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle size={24} style={{ color: 'var(--accent-text)', flexShrink: 0 }} />
                  <div>
                    <h3 className="heading-3 mb-1" style={{ fontSize: '1rem' }}>Same-Day Service Available</h3>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Emergency response for urgent situations</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle size={24} style={{ color: 'var(--accent-text)', flexShrink: 0 }} />
                  <div>
                    <h3 className="heading-3 mb-1" style={{ fontSize: '1rem' }}>100% Eco-Friendly Solutions</h3>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>Safe for children, pets, and the environment</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <CheckCircle size={24} style={{ color: 'var(--accent-text)', flexShrink: 0 }} />
                  <div>
                    <h3 className="heading-3 mb-1" style={{ fontSize: '1rem' }}>Satisfaction Guaranteed</h3>
                    <p className="body-small" style={{ color: 'var(--text-secondary)' }}>If pests return, so do we - free of charge</p>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Side - Lead Form */}
            <div>
              <LeadForm 
                title="Get Your Free Quote" 
                subtitle="Fill out the form and a pest professional will contact you shortly"
                source="home_page"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Google Map Section */}
      <GoogleMap />

      {/* Testimonials Section */}
      <section className="py-20" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">What Our Customers Say</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Real results from real New Yorkers
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map(testimonial => (
              <div key={testimonial.id} className="product-card">
                <div className="flex gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={18} fill="var(--accent-primary)" style={{ color: 'var(--accent-primary)' }} />
                  ))}
                </div>
                <p className="body-medium mb-4">"{testimonial.text}"</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>{testimonial.name}</p>
                    <p className="body-small">{testimonial.location}</p>
                  </div>
                  <span className="caption px-3 py-1 rounded-full" style={{ background: 'var(--accent-wash)', color: 'var(--accent-text)' }}>
                    {testimonial.service}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Badges Section */}
      <TrustBadges />

      {/* Featured In Section */}
      <FeaturedIn />

      {/* SEO-Optimized FAQ Section */}
      <section className="py-20" style={{ background: 'var(--bg-section)' }}>
        <div className="container">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Frequently Asked Questions About NYC Pest Control</h2>
            <p className="body-large" style={{ color: 'var(--text-secondary)' }}>
              Get answers to common questions about our eco-friendly pest control services
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto space-y-6">
            {faqData.map((faq, index) => (
              <details key={index} className="product-card group">
                <summary className="cursor-pointer p-6 flex justify-between items-start">
                  <h3 className="heading-3 pr-4 group-open:text-accent-primary transition-colors">
                    {faq.question}
                  </h3>
                  <div className="transform group-open:rotate-45 transition-transform flex-shrink-0">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                  </div>
                </summary>
                <div className="px-6 pb-6 pt-0">
                  <p className="body-text" style={{ color: 'var(--text-secondary)' }}>
                    {faq.answer}
                  </p>
                </div>
              </details>
            ))}
          </div>
          
          <div className="text-center mt-12">
            <p className="body-large mb-6" style={{ color: 'var(--text-secondary)' }}>
              Have more questions? Our pest control experts are here to help!
            </p>
            <a href={`tel:${companyInfo.phoneRaw}`} className="btn-primary">
              <Phone size={20} />
              Call {companyInfo.phone}
            </a>
          </div>
        </div>
      </section>

      <TestimonialSEO testimonials={testimonials} />
      <UnifiedFAQSEO dynamicFaqs={faqsFromAPI} includeRodentFAQs={true} />

      {/* CTA Section */}
      <section className="py-20" style={{ background: 'var(--bg-page)' }}>
        <div className="container text-center">
          <h2 className="heading-2 mb-4">Ready to Reclaim Your Space?</h2>
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
            Get a free quote today and experience the NY Pest difference
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${companyInfo.phoneRaw}`} className="btn-primary text-lg px-8">
              <Phone size={20} />
              Call {companyInfo.phone}
            </a>
            <Link to="/contact" className="btn-secondary text-lg px-8">
              Request Free Quote
            </Link>
          </div>
        </div>
      </section>

      {/* Social Media Section */}
      <section className="py-16" style={{ background: 'var(--bg-section)' }}>
        <div className="container text-center">
          <h2 className="heading-2 mb-4">Connect With Us</h2>
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>
            Follow us for pest prevention tips, seasonal advice, and customer success stories
          </p>
          
          <div className="flex justify-center items-center gap-6 flex-wrap">
            {/* Facebook */}
            <a 
              href="https://www.facebook.com/nypestexterminating" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-6 py-3 rounded-lg transition-all duration-300 hover:scale-105"
              style={{ 
                background: 'var(--bg-card)', 
                border: '1px solid var(--border-light)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: '#1877F2' }}>
                <svg width="16" height="16" fill="white" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </div>
              <span className="body-small font-semibold">Facebook</span>
            </a>

            {/* Instagram */}
            <a 
              href="https://www.instagram.com/nypestexterminating" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-6 py-3 rounded-lg transition-all duration-300 hover:scale-105"
              style={{ 
                background: 'var(--bg-card)', 
                border: '1px solid var(--border-light)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ 
                background: 'linear-gradient(45deg, #f09433 0%,#e6683c 25%,#dc2743 50%,#cc2366 75%,#bc1888 100%)'
              }}>
                <svg width="16" height="16" fill="white" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                </svg>
              </div>
              <span className="body-small font-semibold">Instagram</span>
            </a>

            {/* LinkedIn */}
            <a 
              href="https://www.linkedin.com/company/nypestexterminating" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-6 py-3 rounded-lg transition-all duration-300 hover:scale-105"
              style={{ 
                background: 'var(--bg-card)', 
                border: '1px solid var(--border-light)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: '#0077B5' }}>
                <svg width="16" height="16" fill="white" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </div>
              <span className="body-small font-semibold">LinkedIn</span>
            </a>

            {/* Yelp */}
            <a 
              href="https://www.yelp.com/biz/ny-pest-exterminating" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-6 py-3 rounded-lg transition-all duration-300 hover:scale-105"
              style={{ 
                background: 'var(--bg-card)', 
                border: '1px solid var(--border-light)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: '#FF1A1A' }}>
                <svg width="16" height="16" fill="white" viewBox="0 0 24 24">
                  <path d="M21.111 18.226c-.141.969-2.119 3.773-3.021 3.773c-.339 0-.712-.146-1.115-.47c-1.688-1.369-5.633-5.188-7.88-8.247c-2.212-3.015-2.656-5.678-.888-7.811c.931-1.125 2.686-1.621 3.818-.945c.23-.446 1.103-.917 1.808-.917c.681 0 1.261.417 1.501.93c1.076-.402 2.334-.126 3.063.794c1.727 2.191 1.328 5.718-.95 8.664c-1.244 1.606-3.501 3.629-6.462 5.28c1.898 1.966 3.734 3.369 5.131 3.993c.406.182.719.236.954.236c.902 0 2.88-2.804 3.021-3.773c.111-.765-.292-1.562-1.111-1.562c-.339 0-.698.119-1.053.307c-.97.514-2.058 1.156-3.201 1.914c2.421-1.263 4.448-2.964 5.529-4.317c1.953-2.444 2.264-5.318.777-7.068c-.537-.632-1.226-.932-1.902-.932c-.255 0-.505.048-.738.14c-.341-.423-.853-.688-1.421-.688c-.542 0-1.032.223-1.392.596c-.89-.363-1.925-.311-2.751.204c-1.397 1.01-1.846 2.84-1.156 4.454c.719 1.682 2.294 3.416 4.342 5.085c.405.33.828.463 1.176.463c1.122 0 3.259-3.278 3.401-4.282c.111-.765-.292-1.562-1.111-1.562z"/>
                </svg>
              </div>
              <span className="body-small font-semibold">Yelp</span>
            </a>

            {/* Google My Business */}
            <a 
              href="https://www.google.com/search?q=NY+Pest+Exterminating" 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex items-center gap-3 px-6 py-3 rounded-lg transition-all duration-300 hover:scale-105"
              style={{ 
                background: 'var(--bg-card)', 
                border: '1px solid var(--border-light)',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}
            >
              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: '#4285F4' }}>
                <svg width="16" height="16" fill="white" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              </div>
              <span className="body-small font-semibold">Google</span>
            </a>
          </div>

          <div className="mt-8 p-4 rounded-lg" style={{ background: 'var(--accent-wash)' }}>
            <p className="body-small font-semibold mb-2" style={{ color: 'var(--accent-text)' }}>
              ⭐ Leave us a review and help other New Yorkers find reliable pest control!
            </p>
            <p className="caption" style={{ color: 'var(--text-secondary)' }}>
              Share your experience on Google, Yelp, or Facebook
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;